import { useState, useEffect } from 'react'
import { Crown, User, Mail, Phone, Calendar, Sparkles, CheckCircle } from 'lucide-react'
import { useCurrentUser } from '@/features/profile/hooks/useCurrentUser'
import { useAccountType, PaywallModal } from '@/features/payments'
import { trackUpgradeCtaClicked } from '@/shared/utils/analytics'
import { fbTrackViewContent } from '@/shared/utils/facebookPixel'

export default function AccountPage() {
  const { user: userProfile } = useCurrentUser()
  const { isPaidUser, isLoading } = useAccountType()
  const [showPaywall, setShowPaywall] = useState(false)

  // Track ViewContent for FB Pixel
  useEffect(() => {
    fbTrackViewContent('account', undefined, 'Account')
  }, [])

  const getDisplayName = () => {
    const firstName = userProfile?.firstName || ''
    const lastName = userProfile?.lastName || ''
    if (firstName && lastName) {
      return `${firstName} ${lastName}`
    }
    if (firstName) {
      return firstName
    }
    return 'Student'
  }

  const getInitials = () => {
    const firstName = userProfile?.firstName || ''
    const lastName = userProfile?.lastName || ''
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase()
    }
    if (firstName) {
      return firstName[0].toUpperCase()
    }
    return 'U'
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Account</h1>
        <p className="text-sm sm:text-base text-gray-600 mt-1">
          Manage your account and subscription
        </p>
      </div>

      {/* Profile Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
        <div className="flex items-center gap-4">
          <div className="relative">
            <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-xl">{getInitials()}</span>
            </div>
            {isPaidUser && (
              <div className="absolute -top-1 -right-1 w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center">
                <Crown className="w-3.5 h-3.5 text-white" />
              </div>
            )}
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{getDisplayName()}</h2>
            {isPaidUser ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-800 text-sm font-medium rounded-full">
                <Crown className="w-3.5 h-3.5" />
                Premium Member
              </span>
            ) : (
              <span className="inline-flex items-center px-2 py-0.5 bg-gray-100 text-gray-600 text-sm font-medium rounded-full">
                Free Plan
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Profile Details */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Profile Details</h3>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <User className="w-5 h-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-500">Name</p>
              <p className="text-gray-900">{getDisplayName()}</p>
            </div>
          </div>
          {userProfile?.email && (
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="text-gray-900">{userProfile.email}</p>
              </div>
            </div>
          )}
          {userProfile?.phoneNumber && (
            <div className="flex items-center gap-3">
              <Phone className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Mobile</p>
                <p className="text-gray-900">{userProfile.phoneNumber}</p>
              </div>
            </div>
          )}
          {userProfile?.createdAt && (
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Member Since</p>
                <p className="text-gray-900">{formatDate(userProfile.createdAt)}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Subscription Section */}
      {false && (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Subscription</h3>

        {isPaidUser ? (
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-4 sm:p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center">
                <Crown className="w-6 h-6 text-white" />
              </div>
              <div>
                <h4 className="text-lg font-semibold text-amber-900">Premium Plan</h4>
                <p className="text-sm text-amber-700">Full access to all features</p>
              </div>
            </div>

            {/* Features */}
            <div className="space-y-2 text-sm text-amber-800">
              <p className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-amber-600" />
                Unlimited PYQ access
              </p>
              <p className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-amber-600" />
                All test series unlocked
              </p>
              <p className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-amber-600" />
                Detailed solutions & explanations
              </p>
              <p className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-amber-600" />
                Advanced analytics
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 sm:p-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-6 h-6 text-gray-500" />
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">Free Plan</h4>
              <p className="text-sm text-gray-600 mb-4">
                Upgrade to Premium to unlock all features and get the most out of your NEET preparation.
              </p>
              <button
                onClick={() => {
                  trackUpgradeCtaClicked('account_page')
                  setShowPaywall(true)
                }}
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Sparkles className="w-4 h-4" />
                Upgrade to Premium
              </button>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Paywall Modal */}
      <PaywallModal
        isOpen={showPaywall}
        onClose={() => setShowPaywall(false)}
        triggerLocation="account_page"
      />
    </div>
  )
}
